import { useState, useEffect, useRef } from 'react';
import styles from './PhotoPickerItem.module.scss';
import SelectedIcon from './images/selected-icon.svg?react';
import EmptyImageIcon from '~/assets/svg/newSvg/empty-image.svg?react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

interface IPhotoPickerItemProps {
  id: number;
  isSelected: boolean;
  image_url: string;
  onImageClick: () => void;
  isFetching: boolean;
}

const PhotoPickerItem = ({
  id,
  isSelected,
  image_url,
  onImageClick,
  isFetching,
}: IPhotoPickerItemProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isBroken, setIsBroken] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setIsBroken(false);

    timerRef.current = setTimeout(() => {
      setIsLoading(prev => {
        if (prev) setIsBroken(true);
        return false;
      });
    }, 15000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [image_url]);

  const handleLoad = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsLoading(false);
  };

  const handleError = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsLoading(false);
    setIsBroken(true);
  };

  return (
    <li key={id} className={styles.photoPickerItem}>
      <button
        type="button"
        onClick={onImageClick}
        className={classNames(
          styles.photoSelect,
          isSelected ? styles.photoSelectSelected : ''
        )}
      >
        {(isLoading || isFetching) && !isBroken && (
          <div className={styles.shimmer} />
        )}
        {isBroken ? (
          <div className={styles.broken}>
            <EmptyImageIcon className={styles.brokenIcon} />
            <span className={styles.brokenText}>{t('failed_to_load')}</span>
          </div>
        ) : (
          <img
            className={styles.photo}
            src={image_url}
            alt=""
            draggable={false}
            onLoad={handleLoad}
            onError={handleError}
          />
        )}
        {isSelected && !isBroken ? (
          <SelectedIcon className={styles.photoSelectedIcon} />
        ) : null}
      </button>
    </li>
  );
};

export default PhotoPickerItem;
